import { useState, useCallback } from 'react'
import { useToken } from 'app/hooks'
import API from 'utils/api'
import * as antd from 'antd'

export interface MerchantStatus {
	memberId?: string
	merchantId?: string
	reviewStatus?: 'W' | 'P' | 'R' | 'S' | 'O' | 'N'
	openCode?: '0000' | '1010' | '1700'
	lastSyncedAt?: string
}

export function usePaySSAMMerchant(deptId?: number) {
	const { data: token } = useToken()
	const [merchantStatus, setMerchantStatus] = useState<MerchantStatus | null>(null)
	const [merchantInfo, setMerchantInfo] = useState<any>(null)
	const [loadingStatus, setLoadingStatus] = useState(false)
	const [loadingInfo, setLoadingInfo] = useState(false)
	const [registering, setRegistering] = useState(false)

	const registerMerchant = useCallback(async () => {
		if (!deptId || !token) {
			throw new Error('지점 정보를 찾을 수 없습니다.')
		}

		setRegistering(true)
		try {
			const res = await API.post(`/payments/payssam/dept/${deptId}/merchant/register`, token + '', {})
			const payload = res.result?.result ?? res.result
			const url = payload?.info?.url
			if (res.result?.success !== false && url) {
				window.open(url, '_blank')
				antd.message.success('연동 페이지가 열렸습니다. 완료 후 상태를 새로고침해주세요.')
				return payload
			}
			throw new Error(res.result?.message || (payload?.msg) || '연동 시작에 실패했습니다.')
		} catch (err: any) {
			const errorMsg = err.message || '연동 시작 중 오류가 발생했습니다.'
			antd.message.error(errorMsg)
			console.error(err)
			throw err
		} finally {
			setRegistering(false)
		}
	}, [deptId, token])

	const refreshStatus = useCallback(async (showMessage: boolean = true) => {
		if (!deptId || !token) return

		setLoadingStatus(true)
		try {
			const res = await API.get(`/payments/payssam/dept/${deptId}/merchant/status`, token + '', {})
			if (res.result?.success) {
				setMerchantStatus(res.result.result)
				if (showMessage) {
					antd.message.success('상태를 새로고침했습니다.')
				}
				return res.result.result
			} else {
				throw new Error(res.result?.message || '상태 조회에 실패했습니다.')
			}
		} catch (err: any) {
			if (showMessage) {
				const errorMsg = err.message || '상태 조회 중 오류가 발생했습니다.'
				antd.message.error(errorMsg)
			}
			console.warn(err)
		} finally {
			setLoadingStatus(false)
		}
	}, [deptId, token])

	const getMerchantInfo = useCallback(async () => {
		if (!deptId || !token) {
			throw new Error('지점 정보를 찾을 수 없습니다.')
		}

		setLoadingInfo(true)
		try {
			const res = await API.get(`/payments/payssam/dept/${deptId}/merchant/info`, token + '', {})
			if (res.result?.success) {
				setMerchantInfo(res.result.result)
				return res.result.result
			}
			const payMsg = res.result?.result?.msg
			throw new Error(payMsg || res.result?.message || '가맹점 정보 조회에 실패했습니다.')
		} catch (err: any) {
			const errorMsg = err.message || '가맹점 정보 조회 중 오류가 발생했습니다.'
			antd.message.error(errorMsg)
			console.error(err)
			throw err
		} finally {
			setLoadingInfo(false)
		}
	}, [deptId, token])

	return {
		merchantStatus,
		merchantInfo,
		loadingStatus,
		loadingInfo,
		registering,
		registerMerchant,
		refreshStatus,
		getMerchantInfo,
	}
}
